/**
 * exportQuestionsCsv.js
 * CSV export utility for quiz questions with headers matching import functionality
 * Handles complex data types including HTML content and JSON fields with proper escaping
 * Headers are consistent with importQuestionsCsv.js EXPECTED_HEADERS for seamless import/export workflow
 */

import { QUESTION_TYPES } from 'utils/constants/question';

/**
 * Maps question type ID to question type name using QUESTION_TYPES constant
 * @param {number} questionTypeId - The question type ID
 * @returns {string} - The question type name or empty string if not found
 */
function getQuestionTypeName(questionTypeId) {
  if (!questionTypeId) return '';

  const questionType = QUESTION_TYPES.find(
    (type) => type.Id === parseInt(questionTypeId, 10)
  );
  return questionType ? questionType.Name : '';
}

/**
 * Converts an array of question objects to CSV format with proper escaping
 * @param {Array} questions - Array of question objects to convert
 * @returns {string|null} - CSV string or null if array is empty
 */
function convertQuestionsToCSV(questions) {
  if (!questions || questions.length === 0) return null;

  const columnDelimiter = ',';
  const lineDelimiter = '\n';

  const headers = [
    'questionTitle',
    'explanation',
    'autoCheckInstruction',
    'multipleChoiceChoicesJson',
    'multipleChoiceAnswersJson',
    'allowPartialPoints',
    'isAllowedAutoCheck',
    'mark',
    'questionTypeName',
    // 'questionCategories', // for future use
  ];

  let result = '';

  // Add the header row
  result += headers.join(columnDelimiter);
  result += lineDelimiter;

  // Transform and add each question row
  questions.forEach((question) => {
    const transformedQuestion = {
      questionTitle: question.questionHtml || '',
      explanation: question.explanation || '',
      autoCheckInstruction: question.autoCheckInstruction || '',
      multipleChoiceChoicesJson: question.multipleChoiceChoices
        ? JSON.stringify(question.multipleChoiceChoices)
        : '',
      multipleChoiceAnswersJson: question.multipleChoiceAnswers
        ? JSON.stringify(question.multipleChoiceAnswers)
        : '',
      allowPartialPoints: question.allowPartialPoints ?? null,
      isAllowedAutoCheck: question.isAllowedAutoCheck ?? null,
      mark: question.mark ?? null,
      questionTypeName: getQuestionTypeName(question.questionTypeId),
      // questionCategories: null, // for future use
    };

    // Create the CSV row with proper escaping
    const row = headers
      .map((header) => {
        const value = transformedQuestion[header];

        // Handle null or undefined values
        if (value === null || value === undefined) {
          return '';
        }

        // Convert to string and escape quotes by doubling them
        const stringValue = value.toString().replace(/"/g, '""');

        // Wrap the value in double quotes if it contains special characters
        if (
          stringValue.indexOf(columnDelimiter) >= 0 ||
          stringValue.indexOf(lineDelimiter) >= 0 ||
          stringValue.indexOf('"') >= 0 ||
          stringValue.indexOf('<') >= 0 || // HTML content
          stringValue.indexOf('>') >= 0 || // HTML content
          stringValue.indexOf('{') >= 0 || // JSON content
          stringValue.indexOf('}') >= 0 // JSON content
        ) {
          return `"${stringValue}"`;
        }

        return stringValue;
      })
      .join(columnDelimiter);

    result += row + lineDelimiter;
  });

  return result;
}

/**
 * Exports questions as CSV file with download
 * @param {Array} questions - Array of question objects to export
 * @param {string} exportType - The file name prefix (default: 'quiz-questions')
 */
export default function exportQuestionsAsCsv(
  questions,
  exportType = 'quiz-questions'
) {
  const csv = convertQuestionsToCSV(questions);

  if (csv === null) return;

  // Generate filename with timestamp
  const filename = `${exportType}.csv`;

  // Create and trigger download
  const link = document.createElement('a');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL
  URL.revokeObjectURL(url);
}
